import {ChoiceProps, IconButton} from 'platform/components';
import {Show, Text, VStack} from 'platform/foundation';

import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps, useBoolean} from 'shared/web';

import {SourcingMobileDialog} from '../../SourcingMobileDialog/SourcingMobileDialog';
import {LiveLastUpdateTime} from './LiveLastUpdateTime';
import {SourcingTableRealTimeSwitch} from './SourcingTableRealTimeSwitch';
import {SourcingTableSortChoice} from './SourcingTableSortChoice';

interface SourcingTableViewSettingsProps extends TestIdProps {
  isRealTimeEnabled?: boolean;
  isRealTime: boolean;
  realTimeTooltip: string;
  onChangeRealTime: (isRealTime: boolean) => void;
  /** Sort and last update describe the list; the map view has neither, so it hides them. */
  hasListSettings: boolean;
  lastUpdateTime?: number | null;
  isPolling?: boolean;
  sortByValue: string | null;
  sortByOptions: ChoiceProps<string>['options'];
  onChangeSortBy: ChoiceProps<string>['onChange'];
}

/**
 * The trigger and the dialog own the open state together, so leaving the card view unmounts it
 * instead of leaving a dialog that reopens itself on the way back.
 */
export function SourcingTableViewSettings(props: SourcingTableViewSettingsProps) {
  const [isOpen, open, close] = useBoolean();

  return (
    <>
      <IconButton
        icon="action/settings"
        aria-label={i18n.t('entity.sourcingTable.labels.viewSettings')}
        onClick={open}
        data-testid={suffixTestId('openViewSettings', props)}
      />
      <SourcingMobileDialog
        isOpen={isOpen}
        onClose={close}
        title={i18n.t('entity.sourcingTable.labels.viewSettings')}
        data-testid={suffixTestId('viewSettingsDialog', props)}
      >
        <VStack spacing={4}>
          <Show when={props.isRealTimeEnabled}>
            <SourcingTableRealTimeSwitch
              value={props.isRealTime}
              tooltip={props.realTimeTooltip}
              onChange={props.onChangeRealTime}
            />
          </Show>
          <Show when={props.isRealTimeEnabled && props.hasListSettings}>
            <LiveLastUpdateTime
              lastUpdateTime={props.lastUpdateTime}
              isLiveUpdateEnabled={props.isRealTime && props.isRealTimeEnabled}
              isPolling={props.isPolling}
            />
          </Show>
          <Show when={props.hasListSettings}>
            <VStack spacing={1}>
              <Text size="xSmall" color="secondary">
                {i18n.t('general.actions.sortBy')}
              </Text>
              <SourcingTableSortChoice
                value={props.sortByValue}
                options={props.sortByOptions}
                onChange={props.onChangeSortBy}
                isFullWidth
                data-testid={props['data-testid']}
              />
            </VStack>
          </Show>
        </VStack>
      </SourcingMobileDialog>
    </>
  );
}
